import { DAY_NAMES } from '../engine'
import type { CheckInV2, PlanBlockV2, PlanDayV2, TopicProgressV2 } from './types'
import { BLOCK_MINS } from './types'
import type { Phase } from './phases'
import { templateFor, partSizes, PART_LABELS } from './templates'
import { buildQueue, type SupplyFn } from './queue'

export interface DayInput {
  date: string
  dayIdx: number
  phase: Phase
  dailyMinutes: number
  isWeekend: boolean
  kelimeEnabled: boolean
  dayStart: string | null
  cycle: string[]
  supply: SupplyFn
  topics: TopicProgressV2[]
  checkIn?: CheckInV2
  doneKeys?: Set<string>
}

function clock(startMin: number): string {
  const h = Math.floor(startMin / 60) % 24
  const m = Math.round(startMin % 60)
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
}

function parseDayStart(dayStart: string | null): number | null {
  if (!dayStart) return null
  const [h, m] = dayStart.split(':').map(Number)
  if (Number.isNaN(h)) return null
  return h * 60 + (m || 0)
}

function assignTimes(blocks: PlanBlockV2[], dayStart: string | null): void {
  const t0 = parseDayStart(dayStart)
  if (t0 == null) return
  let t = t0
  let flowDone = 0
  blocks.forEach((b) => {
    b.time = clock(t)
    t += b.mins + (b.core ? 0 : 10)
    if (!b.core && !b.fixed) {
      flowDone++
      if (flowDone === 4 && t < 13 * 60 + 30) t = 13 * 60 + 30
    }
  })
}

function denemeDayBlocks(denemes: ('tyt' | 'ayt')[]): PlanBlockV2[] {
  const blocks: PlanBlockV2[] = []
  let seq = 1
  const fixed = (b: Omit<PlanBlockV2, 'seq' | 'fixed'>): PlanBlockV2 => ({
    ...b,
    seq: seq++,
    fixed: true,
  })
  if (denemes.includes('tyt')) {
    blocks.push(fixed({ kind: 'deneme', subject: 'TYT denemesi', mins: 135, source: 'system', time: '10:15', denemeKind: 'tyt' }))
    blocks.push(fixed({ kind: 'analiz', subject: 'TYT analizi', mins: BLOCK_MINS.analiz, source: 'system', time: '13:00' }))
  }
  if (denemes.includes('ayt')) {
    blocks.push(fixed({ kind: 'deneme', subject: 'AYT denemesi', mins: 180, source: 'system', time: '15:00', denemeKind: 'ayt' }))
    blocks.push(fixed({ kind: 'analiz', subject: 'AYT analizi', mins: BLOCK_MINS.analiz, source: 'system', time: '18:15' }))
  }
  blocks.push(fixed({ kind: 'netgiris', subject: 'Net girişi', mins: BLOCK_MINS.netgiris, source: 'system', core: true, time: '19:15' }))
  return blocks
}

function minimumDay(cycle: string[], supply: SupplyFn): PlanBlockV2[] {
  const ours = cycle.filter((s) => supply(s).ours)
  const blocks: PlanBlockV2[] = []
  let seq = 1
  const push = (kind: PlanBlockV2['kind'], subject: string, extra: Partial<PlanBlockV2> = {}) => {
    const info = supply(subject)
    blocks.push({
      kind, subject, mins: 20, seq: seq++, light: true,
      source: extra.source || (info.ours ? 'ours' : 'generic'),
      lessonId: info.ours ? (info.lessonId ?? undefined) : undefined,
      lessonTitle: info.ours ? (info.lessonTitle ?? undefined) : undefined,
      ...extra,
    })
  }
  const s1 = ours[0] || 'Matematik'
  const s2 = ours[1] || ours[0] || 'Matematik'
  push('soru', s1, { source: supply(s1).ours ? 'ours' : 'generic' })
  push('soru', s2, { source: supply(s2).ours ? 'ours' : 'generic' })
  push('tekrar', 'Dünkü konular', { source: 'system' })
  push('video', s1, { source: supply(s1).ours ? 'ours' : 'generic', light: true })
  return blocks
}

export function assembleDay(input: DayInput): PlanDayV2 {
  const {
    date, dayIdx, phase, dailyMinutes, isWeekend, kelimeEnabled, dayStart,
    cycle, supply, topics, checkIn, doneKeys,
  } = input
  const dow = dayIdx % 7
  const isSaturday = dow === 5
  const isSunday = dow === 6
  const denemeMode = isSaturday && phase >= 3

  const coreHead: PlanBlockV2[] = [
    { kind: 'plan', subject: 'Günün planı', mins: BLOCK_MINS.plan, seq: 1, source: 'system', core: true },
  ]

  let flow: PlanBlockV2[]
  let kind: PlanDayV2['kind'] = 'normal'
  if (checkIn?.minDay && checkIn.checkedIn) {
    flow = minimumDay(cycle, supply)
    kind = 'minimum'
  } else if (denemeMode) {
    flow = denemeDayBlocks(phase >= 3 ? ['tyt', 'ayt'] : ['tyt'])
    kind = 'deneme'
  } else {
    const tpl = templateFor(dailyMinutes, isWeekend)
    let slots = tpl.flowSlots
    if (isSaturday && phase < 3) {
      const first = supply(cycle[0] || 'Matematik')
      const deneme: PlanBlockV2 = {
        kind: 'deneme', subject: `${cycle[0] || 'Matematik'} denemesi`, mins: 100, seq: 0,
        source: first.ours ? 'ours' : 'generic', fixed: true, denemeKind: 'brans',
      }
      const analiz: PlanBlockV2 = { kind: 'analiz', subject: 'Deneme analizi', mins: 30, seq: 0, source: 'system', fixed: true }
      flow = [deneme, analiz, ...buildQueue({ date, slots: Math.max(1, slots - 2), phase, cycle, supply, topics })]
    } else if (isSunday) {
      slots = Math.min(slots, 4)
      flow = buildQueue({ date, slots, phase, cycle, supply, topics })
      flow = flow.map((b) => (b.kind === 'video' ? { ...b, kind: 'tekrar' as const, lessonId: undefined, mins: BLOCK_MINS.tekrar } : b))
      kind = 'tekrar'
    } else {
      flow = buildQueue({ date, slots, phase, cycle, supply, topics })
    }
  }

  const coreTail: PlanBlockV2[] = [
    { kind: 'paragraf', subject: 'Paragraf', mins: BLOCK_MINS.paragraf, seq: 0, source: 'system', core: true },
    { kind: 'yanlis', subject: 'Yanlış defteri', mins: BLOCK_MINS.yanlis, seq: 0, source: 'system', core: true },
  ]
  if (kelimeEnabled) {
    coreTail.push({ kind: 'kelime', subject: 'Kelime', mins: BLOCK_MINS.kelime, seq: 0, source: 'system', core: true, light: true })
  }
  if (isSunday) {
    coreTail.push({ kind: 'plan', subject: 'Haftalık değerlendirme', mins: 15, seq: 0, source: 'system', core: true })
  }

  let blocks: PlanBlockV2[]
  if (denemeMode) {
    blocks = [...flow, ...coreTail]
  } else {
    blocks = [...coreHead, ...flow, ...coreTail]
  }
  blocks = blocks.map((b, i) => ({ ...b, seq: i + 1 }))

  const sizes = partSizes(blocks.length)
  const groups: PlanDayV2['groups'] = []
  let k = 0
  sizes.forEach((sz, i) => {
    groups.push({ label: i < PART_LABELS.length ? PART_LABELS[i] : null, blocks: blocks.slice(k, k + sz) })
    k += sz
  })

  const key = (b: PlanBlockV2) => `${date}#${b.seq}`
  if (doneKeys) {
    blocks.forEach((b) => {
      if (doneKeys.has(key(b))) b.done = true
    })
  }
  assignTimes(blocks, denemeMode ? null : dayStart)

  return {
    day: DAY_NAMES[dow],
    date,
    kind,
    blocks,
    groups,
    nextSeq: blocks.length,
    totalMins: blocks.reduce((a, b) => a + b.mins, 0),
  }
}

export { clock as blockClock }
